import React from 'react';
import './Document.css';

const TermsOfService = () => {
  return (
    <div className="document-container">
      <div className="document-content">
        <h1>Terms of Service</h1>
        <p className="last-updated"><strong>Last Updated: January 2025</strong></p>

        <section>
          <h2>1. Acceptance of Terms</h2>
          <p>
            By using the Jo Jo Nickname Changer Bot ("the Bot", "we", "us", "our"), you agree to be bound by these Terms of Service ("Terms"). If you do not agree to these Terms, please do not use the Bot.
          </p>
        </section>

        <section>
          <h2>2. Description of Service</h2>
          <p>The Bot is a Discord bot that provides nickname management services, including:</p>
          <ul>
            <li>Automated nickname change requests</li>
            <li>Nickname approval system</li>
            <li>Word filtering and moderation tools</li>
            <li>Nickname history tracking</li>
            <li>Monitoring and reporting features</li>
          </ul>
        </section>

        <section>
          <h2>3. Eligibility</h2>
          <p>You must:</p>
          <ul>
            <li>Be at least 13 years of age (or the minimum age required in your jurisdiction)</li>
            <li>Have a valid Discord account</li>
            <li>Comply with Discord's Terms of Service</li>
            <li>Have proper permissions in the Discord server where you use the Bot</li>
          </ul>
        </section>

        <section>
          <h2>4. User Responsibilities</h2>
          
          <h3>4.1 Appropriate Use</h3>
          <p>You agree to:</p>
          <ul>
            <li>Use the Bot only for lawful purposes</li>
            <li>Not attempt to abuse, exploit, or harm the Bot</li>
            <li>Not use the Bot to violate Discord's Terms of Service or Community Guidelines</li>
            <li>Not use the Bot to harass, threaten, or harm others</li>
            <li>Not attempt to bypass security measures or filters</li>
          </ul>

          <h3>4.2 Nickname Content</h3>
          <p>You agree that:</p>
          <ul>
            <li>Nicknames must comply with Discord's Terms of Service</li>
            <li>Nicknames must not contain offensive, inappropriate, or prohibited content</li>
            <li>Nicknames must not impersonate others or violate intellectual property rights</li>
            <li>The Bot owner and server moderators reserve the right to reject any nickname request</li>
          </ul>

          <h3>4.3 Server Administration</h3>
          <p>If you are a server administrator:</p>
          <ul>
            <li>You are responsible for configuring the Bot appropriately</li>
            <li>You must ensure users understand how the Bot works</li>
            <li>You are responsible for moderating nickname requests in your server</li>
            <li>You must comply with all applicable laws and regulations</li>
          </ul>
        </section>

        <section>
          <h2>5. Bot Availability</h2>
          
          <h3>5.1 Service Availability</h3>
          <ul>
            <li>The Bot is provided "as is" without warranties of any kind</li>
            <li>We do not guarantee uninterrupted or error-free service</li>
            <li>The Bot may be temporarily unavailable for maintenance or updates</li>
            <li>We reserve the right to modify or discontinue the Bot at any time</li>
          </ul>

          <h3>5.2 Uptime</h3>
          <ul>
            <li>While we strive for high availability, we do not guarantee specific uptime percentages</li>
            <li>The Bot includes a heartbeat monitoring system, but outages may occur</li>
          </ul>
        </section>

        <section>
          <h2>6. Data and Privacy</h2>
          
          <h3>6.1 Data Collection</h3>
          <p>The Bot collects and stores the following data:</p>
          <ul>
            <li>User IDs</li>
            <li>Guild (server) IDs</li>
            <li>Channel IDs</li>
            <li>Nickname change history</li>
            <li>Timestamps of actions</li>
            <li>Filtered words and moderation data</li>
          </ul>

          <h3>6.2 Data Storage</h3>
          <ul>
            <li>Data is stored locally in JSON files</li>
            <li>Data is retained for operational purposes</li>
            <li>Some data may be retained indefinitely for history tracking</li>
          </ul>
          <p>For more details, please see our <a href="/privacy">Privacy Policy</a>.</p>
        </section>

        <section>
          <h2>7. Intellectual Property</h2>
          
          <h3>7.1 Bot Ownership</h3>
          <ul>
            <li>The Bot and its code are the property of the Bot owner</li>
            <li>You may not copy, modify, or distribute the Bot without permission</li>
            <li>The Bot is provided for use, not ownership</li>
          </ul>

          <h3>7.2 User Content</h3>
          <ul>
            <li>You retain ownership of content you submit (e.g., nicknames)</li>
            <li>By using the Bot, you grant us permission to process and store your data as necessary for the Bot's operation</li>
          </ul>
        </section>

        <section>
          <h2>8. Moderation and Enforcement</h2>
          
          <h3>8.1 Content Filtering</h3>
          <ul>
            <li>The Bot includes word filtering systems</li>
            <li>Filtered words may be added globally or per-server</li>
            <li>The Bot owner reserves the right to add words to the global filter</li>
          </ul>

          <h3>8.2 Violations</h3>
          <p>If you violate these Terms:</p>
          <ul>
            <li>Your nickname requests may be rejected</li>
            <li>You may be blocked from using the Bot</li>
            <li>Server administrators may remove the Bot from their server</li>
            <li>We may report violations to Discord</li>
          </ul>
        </section>

        <section>
          <h2>9. Limitation of Liability</h2>
          
          <h3>9.1 Disclaimer</h3>
          <p>TO THE MAXIMUM EXTENT PERMITTED BY LAW:</p>
          <ul>
            <li>THE BOT IS PROVIDED "AS IS" WITHOUT WARRANTIES</li>
            <li>WE ARE NOT LIABLE FOR ANY DAMAGES ARISING FROM USE OF THE BOT</li>
            <li>WE ARE NOT RESPONSIBLE FOR CONTENT CREATED BY USERS</li>
            <li>WE ARE NOT RESPONSIBLE FOR ACTIONS TAKEN BY SERVER ADMINISTRATORS</li>
          </ul>

          <h3>9.2 Indemnification</h3>
          <p>You agree to indemnify and hold harmless the Bot owner from any claims, damages, or expenses arising from:</p>
          <ul>
            <li>Your use of the Bot</li>
            <li>Your violation of these Terms</li>
            <li>Your violation of any laws or regulations</li>
            <li>Content you submit through the Bot</li>
          </ul>
        </section>

        <section>
          <h2>10. Changes to Terms</h2>
          
          <h3>10.1 Modifications</h3>
          <ul>
            <li>We reserve the right to modify these Terms at any time</li>
            <li>Material changes will be communicated through the Bot or our support channels</li>
            <li>Continued use of the Bot after changes constitutes acceptance</li>
          </ul>

          <h3>10.2 Notification</h3>
          <ul>
            <li>We will make reasonable efforts to notify users of significant changes</li>
            <li>The "Last Updated" date at the top indicates when Terms were last revised</li>
          </ul>
        </section>

        <section>
          <h2>11. Termination</h2>
          
          <h3>11.1 By You</h3>
          <ul>
            <li>You may stop using the Bot at any time</li>
            <li>Server administrators may remove the Bot from their server at any time</li>
          </ul>

          <h3>11.2 By Us</h3>
          <p>We may terminate or suspend your access to the Bot if:</p>
          <ul>
            <li>You violate these Terms</li>
            <li>You violate Discord's Terms of Service</li>
            <li>We discontinue the Bot</li>
            <li>Required by law or regulation</li>
          </ul>
        </section>

        <section>
          <h2>12. Discord Terms Compliance</h2>
          
          <h3>12.1 Discord's Terms</h3>
          <ul>
            <li>This Bot is a third-party service and is not affiliated with Discord</li>
            <li>You must comply with Discord's Terms of Service when using this Bot</li>
            <li>Discord's Terms take precedence in case of conflict</li>
          </ul>

          <h3>12.2 Bot Permissions</h3>
          <ul>
            <li>The Bot requires certain permissions to function (e.g., Manage Nicknames)</li>
            <li>Granting permissions is at your discretion</li>
            <li>The Bot will only use permissions necessary for its functions</li>
          </ul>
        </section>

        <section>
          <h2>13. Support and Contact</h2>
          
          <h3>13.1 Support</h3>
          <ul>
            <li>Support is provided on a best-effort basis</li>
            <li>We do not guarantee response times</li>
            <li>Support may be limited or unavailable at times</li>
          </ul>

          <h3>13.2 Contact</h3>
          <p>For questions about these Terms, contact:</p>
          <ul>
            <li><strong>Bot Owner:</strong> Joynal Bokhsho</li>
            <li><strong>GitHub:</strong> <a href="https://github.com/joynalbokhsho" target="_blank" rel="noopener noreferrer">@joynalbokhsho</a></li>
          </ul>
        </section>

        <section>
          <h2>14. Governing Law</h2>
          <p>These Terms are governed by applicable laws. Any disputes will be resolved in accordance with applicable legal procedures.</p>
        </section>

        <section>
          <h2>15. Severability</h2>
          <p>If any provision of these Terms is found to be invalid or unenforceable, the remaining provisions will continue in full force and effect.</p>
        </section>

        <section>
          <h2>16. Entire Agreement</h2>
          <p>These Terms, together with our Privacy Policy, constitute the entire agreement between you and the Bot owner regarding the use of the Bot.</p>
        </section>

        <section>
          <h2>17. Acknowledgment</h2>
          <p>By using the Bot, you acknowledge that:</p>
          <ul>
            <li>You have read and understood these Terms</li>
            <li>You agree to be bound by these Terms</li>
            <li>You are responsible for compliance with all applicable laws</li>
          </ul>
        </section>

        <div className="document-footer">
          <p><strong>By using the Jo Jo Bot, you agree to these Terms of Service.</strong></p>
        </div>
      </div>
    </div>
  );
};

export default TermsOfService;

